import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { CustomizableReportTable } from '../reports/CustomizableReportTable';
import { fmtUnits } from './reconColumns';
import { useAccountColumns } from './useAccountColumns';

const STATUSES = ['Matched', 'Mismatch', 'Not in Portal', 'Not in BenPos'];
const KPI_STYLE = { Matched: 'text-emerald-600', Mismatch: 'text-red-600', 'Not in Portal': 'text-amber-600', 'Not in BenPos': 'text-orange-600' };
const DEFAULT = ['pan', 'account_name', 'benpos_name', 'isin', 'class_name', 'portal_units', 'benpos_units', 'difference', 'status', 'bank_status', 'ifsc_status'];

const Kpi = ({ label, value, cls, active, onClick, testId }) => (
  <button onClick={onClick} data-testid={testId}
    className={`text-left bg-white border rounded-lg p-4 transition-colors ${active ? 'border-teal-500 ring-1 ring-teal-500' : 'hover:border-gray-300'}`}>
    <p className="text-xs text-gray-500 uppercase tracking-wider">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${cls || 'text-gray-900'}`}>{value}</p>
  </button>
);

const IsinSummary = ({ items }) => (
  <div className="bg-white border rounded-lg mb-6" data-testid="isin-summary">
    <div className="px-4 py-3 border-b font-semibold text-sm text-gray-800">ISIN / Class-wise Summary</div>
    <table className="w-full text-sm">
      <thead><tr className="bg-gray-50 text-left text-gray-600"><th className="px-4 py-2">ISIN</th><th className="px-4 py-2">Class</th><th className="px-4 py-2 text-right">Units per Portal</th><th className="px-4 py-2 text-right">Units per BenPos</th><th className="px-4 py-2 text-right">Difference</th><th className="px-4 py-2 text-right">Investors</th><th className="px-4 py-2 text-right">Exceptions</th></tr></thead>
      <tbody>
        {items.map((s, i) => (
          <tr key={i} className="border-t" data-testid={`isin-summary-row-${i}`}>
            <td className="px-4 py-2 font-mono text-xs">{s.isin || <span className="text-amber-600">No ISIN</span>}</td>
            <td className="px-4 py-2">{s.class_name || '—'}</td>
            <td className="px-4 py-2 text-right tabular-nums">{fmtUnits(s.portal_units)}</td>
            <td className="px-4 py-2 text-right tabular-nums">{fmtUnits(s.benpos_units)}</td>
            <td className={`px-4 py-2 text-right tabular-nums ${Math.abs(s.difference) > 0.0001 ? 'text-red-600 font-semibold' : ''}`}>{fmtUnits(s.difference)}</td>
            <td className="px-4 py-2 text-right">{s.investors}</td>
            <td className="px-4 py-2 text-right">{s.exceptions}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const ReconciliationTab = ({ recon }) => {
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const { summary, rows, isin_summary: isinSummary, upload } = recon;

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter((r) => (!status || r.status === status)
      && (!q || [r.pan, r.account_name, r.benpos_name, r.isin, r.folio_no].some((v) => (v || '').toLowerCase().includes(q))));
  }, [rows, status, search]);
  const { columns, rows: viewRows, onVisibleChange } = useAccountColumns(recon, filtered);

  return (
    <div data-testid="recon-tab">
      <p className="text-xs text-gray-500 mb-3" data-testid="recon-snapshot-info">Snapshot as on <b>{upload.as_on_date}</b> · {upload.file_name} · matched by PAN + ISIN · Difference = Portal − BenPos</p>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Kpi label="All Rows" value={summary.total_rows} active={!status} onClick={() => setStatus('')} testId="recon-kpi-all" />
        {STATUSES.map((s) => (
          <Kpi key={s} label={s} value={summary[s]} cls={KPI_STYLE[s]} active={status === s} onClick={() => setStatus(status === s ? '' : s)} testId={`recon-kpi-${s.toLowerCase().replace(/ /g, '-')}`} />
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6 text-sm">
        <div className="bg-gray-50 border rounded-lg px-4 py-3">Total Units per Portal: <b data-testid="recon-total-portal">{fmtUnits(summary.portal_units)}</b></div>
        <div className="bg-gray-50 border rounded-lg px-4 py-3">Total Units per BenPos: <b data-testid="recon-total-benpos">{fmtUnits(summary.benpos_units)}</b></div>
        <div className="bg-gray-50 border rounded-lg px-4 py-3">Bank / IFSC mismatches: <b className="text-red-600" data-testid="recon-bank-mismatch">{summary.bank_mismatch} / {summary.ifsc_mismatch}</b></div>
      </div>
      <IsinSummary items={isinSummary} />
      {columns && <CustomizableReportTable
        columns={columns} rows={viewRows} defaultVisible={DEFAULT} storageKey="report-cols-demat-recon" onVisibleChange={onVisibleChange}
        fileName={`benpos_reconciliation_${upload.as_on_date}`} testId="recon" rowKey={(r, i) => `${r.pan}-${r.isin}-${i}`}
        toolbar={(
          <div className="relative min-w-[260px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search PAN, name, ISIN, folio..." className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm" data-testid="recon-search" />
          </div>
        )}
      />}
    </div>
  );
};
